import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid-pro'
import { type GridRowModelUpdate } from '@mui/x-data-grid/models/gridRows'
import WrappedDataGrid from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid'
import { MODE_OPTIONS } from '../../components/data-grid/wrapped-data-grid/wrapped-data-grid-types'
import {
  type CoinAssetData, type AccountAssetData, type PortfolioBalanceData
} from '../../models/asset/asset-types'
import { createCoinBalanceRows, extractForHeaderInitialExposure as extractInitialBalanceByCoin } from '../../utils/view-specific/balance/coin-balance-utils'
import { createAccountBalanceRows, extractForHeaderInitialExposure as extractInitialBalanceByAccount } from '../../utils/view-specific/balance/account-balance-utils'
import { BALANCE_MODE_OPTIONS_CONFIG } from './balance-data-grid-col-definitions'

export interface BalanceDataGridRow extends GridRowModelUpdate {
  id: string
  path: string[]
  coin: string
  account: string
  account_type: string
  account_balance: string
  price_usd: string
  balance_coin: string
  balance_usd: string
  initial_balance_coin: string
  pnl_coin: string
  pnl_usd: string
}

interface BalanceDataGridProps {
  portfolio: string
}

const BalanceDataGrid: React.FC<BalanceDataGridProps> = ({ portfolio }) => {
  const [mode, setMode] = useState<MODE_OPTIONS>(MODE_OPTIONS.COIN)

  const modeConfig = BALANCE_MODE_OPTIONS_CONFIG[mode]
  const modeEndpoint: string | undefined = modeConfig.endpoint
  const modeColumns: GridColDef[] = modeConfig.columns
  const modeAutosizeOptions = modeConfig.autosizeOptions
  const modeSortModel = modeConfig.sortModel

  const { data, error, mutate } = useSWR<PortfolioBalanceData, boolean>(
    modeEndpoint,
    basicFetcher,
    {
      shouldRetryOnError: true,
      refreshInterval: 5000
    }
  )

  const [rows, setRows] = useState<BalanceDataGridRow[]>([])
  const [timestamp, setTimestamp] = useState<number>(0)
  const [totalCurrAsset, setTotalCurrAsset] = useState<string>('')
  const [pnlAsset, setPnLAsset] = useState<string>('')
  const [initialBalanceByCoin, setInitialBalanceByCoin] = useState<Record<string, string>>({})
  const [sortModel, setSortModel] = React.useState<GridSortModel>(modeSortModel)

  useEffect(() => {
    if (error === true || data === undefined) {
      setRows([])
      setTotalCurrAsset('')
      setPnLAsset('')
      setInitialBalanceByCoin({})
    } else {
      const portfolioData = data[portfolio]
      switch (mode) {
        case 'coin':
          setRows(createCoinBalanceRows(portfolioData as CoinAssetData))
          setInitialBalanceByCoin(extractInitialBalanceByCoin(portfolioData as CoinAssetData))
          break
        case 'account':
          setRows(createAccountBalanceRows(portfolioData as AccountAssetData))
          setInitialBalanceByCoin(extractInitialBalanceByAccount(portfolioData as AccountAssetData))
          break
      }

      setTotalCurrAsset(portfolioData.total_curr_asset)
      setPnLAsset(portfolioData.pnl_asset)
      setTimestamp(data.timestamp)

      setSortModel(modeSortModel)
    }
  }, [mode, data, error])

  return (
    <WrappedDataGrid
      gridData={{
        rows,
        columns: modeColumns,
        autosizeOptions: modeAutosizeOptions,
        sortModel,
        portfolio,
        csvOptions: { fileName: portfolio + '_balance' }
      }}
      gridMode={{
        mode,
        modeOptions: Object.values(MODE_OPTIONS).filter(mode => mode === 'coin' || mode === 'account'),
        setMode,
      }}
      toolbarData={{
        timestamp,
        totalCurrAsset,
        pnlAsset,
        initialBalanceByCoin,
        nextFund: '',
      }}
      styleData={{
        background: 'rgba(0, 48, 99, 0.25)',
      }}
      mutate={mutate}
    />
  )
}

export default BalanceDataGrid
